/**
 * CustomerLeadDetailsPage — sub-page for /moves/:quoteId/customer-lead
 *
 * Displays editable cards for:
 *   • Customer (name, phone, email)
 *   • Company (company name, number of employees)
 *   • Lead Info (how they found us, inbound channel, created date)
 *   • Billing Details (name / same-as-POC, address / same-as-origin)
 *
 * Auto-saves on change (debounced 400ms) when in draft status.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, LoaderCircle } from 'lucide-react';
import { type CountryCode } from 'libphonenumber-js';
import { useSharedQuote } from '../../context/QuoteContext';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PhoneInput, parseStoredPhone, phoneToE164 } from '../../components/ui/PhoneInput';
import { CustomDropdown } from '../../components/ui/CustomDropdown';
import {
  DiscoverySource,
  InboundChannel,
  DISCOVERY_SOURCE_LABEL,
  INBOUND_CHANNEL_LABEL,
} from '../../types/admin';

// ── Dropdown options ──────────────────────────────────────────────────────────

const ALL_DISCOVERY_SOURCES: DiscoverySource[] = [
  'google', 'yelp', 'homeadvisor_angi', 'thumbtack', 'nextdoor',
  'social_media', 'repeat_customer', 'referral', 'trade_show_event', 'other',
];

const DISCOVERY_SOURCE_OPTIONS = [
  { value: '', label: 'Select a source…' },
  ...ALL_DISCOVERY_SOURCES.map(s => ({ value: s as string, label: DISCOVERY_SOURCE_LABEL[s] })),
];

const ALL_INBOUND_CHANNELS: InboundChannel[] = [
  'manual_entry', 'booking_form', 'website_form', 'phone_call_inbound',
  'walk_in', 'partner_broker',
];

const INBOUND_CHANNEL_OPTIONS = [
  { value: '', label: 'Select a channel…' },
  ...ALL_INBOUND_CHANNELS.map(c => ({ value: c as string, label: INBOUND_CHANNEL_LABEL[c] })),
];

// ── Field helpers ─────────────────────────────────────────────────────────────

function FieldLabel({
  htmlFor,
  children,
  required,
  optional,
}: {
  htmlFor: string;
  children: React.ReactNode;
  required?: boolean;
  optional?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className="block text-[11px] font-bold uppercase tracking-widest text-slate">
      {children}
      {required && <span className="text-rose ml-0.5">*</span>}
      {optional && <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>}
    </label>
  );
}

function TextInput({
  id,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      id={id}
      type={type}
      autoComplete="off"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                 text-small text-midnight placeholder:text-slate
                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                 transition-colors duration-150"
    />
  );
}

function Checkbox({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 cursor-pointer select-none">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/20 cursor-pointer"
      />
      <span className="text-small text-midnight">{label}</span>
    </label>
  );
}

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm flex flex-col gap-4">
      {title && (
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate border-b border-stone pb-3 -mb-1">
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function CustomerLeadDetailsPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { quote, loading, updateQuote } = useSharedQuote();

  // Form state
  const [initialized, setInitialized] = useState(false);
  const justInitialized = useRef(false);

  // Customer
  const [pocName, setPocName] = useState('');
  const [pocPhoneCountry, setPocPhoneCountry] = useState<CountryCode>('US');
  const [pocPhone, setPocPhone] = useState('');
  const [pocEmail, setPocEmail] = useState('');

  // Company
  const [companyName, setCompanyName] = useState('');
  const [numEmployees, setNumEmployees] = useState('');

  // Lead Info
  const [discoverySource, setDiscoverySource] = useState('');
  const [referredBy, setReferredBy] = useState('');
  const [inboundChannel, setInboundChannel] = useState('');

  // Billing
  const [billingSameAsPoc, setBillingSameAsPoc] = useState(false);
  const [billingName, setBillingName] = useState('');
  const [billingSameAsOrigin, setBillingSameAsOrigin] = useState(false);
  const [billingAddr1, setBillingAddr1] = useState('');
  const [billingAddr2, setBillingAddr2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize form from quote data
  if (!loading && quote && !initialized) {
    justInitialized.current = true;

    // Customer
    setPocName(quote.pointOfContact?.name ?? '');
    const { country, national } = parseStoredPhone(quote.pointOfContact?.phone);
    setPocPhoneCountry(country);
    setPocPhone(national);
    setPocEmail(quote.pointOfContact?.email ?? '');

    // Company
    setCompanyName(quote.customerCompanyName ?? '');
    setNumEmployees(quote.numberOfEmployees != null ? String(quote.numberOfEmployees) : '');

    // Lead Info
    setDiscoverySource(quote.discoverySource ?? '');
    setReferredBy(quote.referredBy ?? '');
    setInboundChannel(quote.inboundChannel ?? '');

    // Billing
    const bi = quote.billingInfo;
    setBillingSameAsPoc(bi?.sameAsPointOfContact ?? false);
    setBillingName(bi?.name ?? '');
    setBillingSameAsOrigin(bi?.sameAsOriginAddress ?? false);
    setBillingAddr1(bi?.addressLine1 ?? '');
    setBillingAddr2(bi?.addressLine2 ?? '');
    setBillingCity(bi?.city ?? '');
    setBillingState(bi?.state ?? '');
    setBillingZip(bi?.zipCode ?? '');

    setInitialized(true);
  }

  const isResidential = quote?.type === 'residential';

  const buildPatch = useCallback(() => {
    const empNum = numEmployees ? parseInt(numEmployees, 10) : undefined;
    const phoneE164 = pocPhone ? phoneToE164(pocPhone, pocPhoneCountry) : undefined;
    const patch: Record<string, unknown> = {
      pointOfContact: {
        name: pocName.trim() || undefined,
        phone: phoneE164 || undefined,
        email: pocEmail.trim() || undefined,
      },
      billingInfo: {
        sameAsPointOfContact: billingSameAsPoc,
        name: billingSameAsPoc ? undefined : billingName.trim() || undefined,
        sameAsOriginAddress: billingSameAsOrigin,
        addressLine1: billingSameAsOrigin ? undefined : billingAddr1.trim() || undefined,
        addressLine2: billingSameAsOrigin ? undefined : billingAddr2.trim() || undefined,
        city: billingSameAsOrigin ? undefined : billingCity.trim() || undefined,
        state: billingSameAsOrigin ? undefined : billingState.trim() || undefined,
        zipCode: billingSameAsOrigin ? undefined : billingZip.trim() || undefined,
      },
      discoverySource: discoverySource || undefined,
      referredBy: discoverySource === 'referral' ? referredBy.trim() || undefined : undefined,
      inboundChannel: inboundChannel || undefined,
    };
    if (isResidential) {
      patch.customerCompanyName = pocName.trim();
    } else {
      patch.customerCompanyName = companyName.trim();
      patch.numberOfEmployees = empNum && !isNaN(empNum) && empNum >= 0 ? empNum : undefined;
    }
    return patch;
  }, [
    pocName, pocPhoneCountry, pocPhone, pocEmail, isResidential,
    companyName, numEmployees,
    discoverySource, referredBy, inboundChannel,
    billingSameAsPoc, billingName, billingSameAsOrigin,
    billingAddr1, billingAddr2, billingCity, billingState, billingZip,
  ]);

  // Debounced auto-save (400ms)
  useEffect(() => {
    if (!initialized) return;
    if (justInitialized.current) {
      justInitialized.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      setSaving(true);
      setError('');
      updateQuote(buildPatch())
        .then(() => setSaving(false))
        .catch(err => {
          setError(err instanceof Error ? err.message : 'Auto-save failed');
          setSaving(false);
        });
    }, 400);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [initialized, buildPatch, updateQuote]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  const showReferredBy = discoverySource === 'referral' || (!discoverySource && quote.leadSource === 'referral');

  return (
    <div className="px-4 pt-6 pb-10">
      {/* Back — mobile only */}
      <button
        type="button"
        onClick={() => navigate(`/moves/${quoteId}`)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-h1 text-midnight">Customer & Lead Details</h1>
        {saving && <LoaderCircle size={16} className="text-slate animate-spin shrink-0" />}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{error}</p>
        </div>
      )}

      <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-5 md:items-start">

        {/* ── Customer ──────────────────────────────────────────────── */}
        <Section title="Customer">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="cld-poc-name" required>Name</FieldLabel>
            <TextInput
              id="cld-poc-name"
              value={pocName}
              onChange={setPocName}
              placeholder="e.g. Jane Smith"
            />
          </div>

          <div className="flex flex-col gap-1.5 md:max-w-xs">
            <FieldLabel htmlFor="cld-poc-phone" optional>Phone</FieldLabel>
            <PhoneInput
              id="cld-poc-phone"
              country={pocPhoneCountry}
              value={pocPhone}
              onCountryChange={setPocPhoneCountry}
              onValueChange={setPocPhone}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="cld-poc-email" optional>Email</FieldLabel>
            <TextInput
              id="cld-poc-email"
              type="email"
              value={pocEmail}
              onChange={setPocEmail}
              placeholder="e.g. jane@example.com"
            />
          </div>
        </Section>

        {/* ── Company ───────────────────────────────────────────────── */}
        <Section title="Company">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="cld-company" required>Company Name</FieldLabel>
            <TextInput
              id="cld-company"
              value={isResidential ? pocName : companyName}
              onChange={isResidential ? setPocName : setCompanyName}
              placeholder="e.g. Acme Corp"
            />
          </div>

          {!isResidential && (
            <div className="flex flex-col gap-1.5 md:max-w-[180px]">
              <FieldLabel htmlFor="cld-employees" optional>Number of Employees</FieldLabel>
              <TextInput
                id="cld-employees"
                type="number"
                value={numEmployees}
                onChange={setNumEmployees}
                placeholder="e.g. 150"
              />
            </div>
          )}
        </Section>

        {/* ── Lead Info ──────────────────────────────────────────────── */}
        <Section title="Lead Info">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="cld-source" optional>How They Found Us</FieldLabel>
            <CustomDropdown
              options={DISCOVERY_SOURCE_OPTIONS}
              value={discoverySource}
              onChange={v => setDiscoverySource(v)}
              placeholder="Select a source…"
            />
          </div>

          {showReferredBy && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="cld-referred-by" optional>Referred By</FieldLabel>
              <TextInput
                id="cld-referred-by"
                value={referredBy}
                onChange={setReferredBy}
                placeholder="Who referred this lead?"
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="cld-channel" optional>Inbound Channel</FieldLabel>
            <CustomDropdown
              options={INBOUND_CHANNEL_OPTIONS}
              value={inboundChannel}
              onChange={v => setInboundChannel(v)}
              placeholder="Select a channel…"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="cld-created" optional>Created</FieldLabel>
            <p className="text-small text-midnight py-1">
              {quote.createdAt
                ? new Date(quote.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—'}
              {quote.createdByName && <span className="text-slate ml-1">by {quote.createdByName}</span>}
            </p>
          </div>
        </Section>

        {/* ── Billing Details ───────────────────────────────────────── */}
        <Section title="Billing Details">
          <Checkbox
            id="cld-billing-same-poc"
            checked={billingSameAsPoc}
            onChange={setBillingSameAsPoc}
            label="Same as point of contact"
          />

          {!billingSameAsPoc && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="cld-billing-name" optional>Name</FieldLabel>
              <TextInput
                id="cld-billing-name"
                value={billingName}
                onChange={setBillingName}
                placeholder="e.g. Billing Department"
              />
            </div>
          )}

          <Checkbox
            id="cld-billing-same-origin"
            checked={billingSameAsOrigin}
            onChange={setBillingSameAsOrigin}
            label="Same as origin address"
          />

          {!billingSameAsOrigin && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="cld-billing-addr1" optional>Address Line 1</FieldLabel>
                <TextInput
                  id="cld-billing-addr1"
                  value={billingAddr1}
                  onChange={setBillingAddr1}
                  placeholder="e.g. 123 Main St"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <FieldLabel htmlFor="cld-billing-addr2" optional>Address Line 2</FieldLabel>
                <TextInput
                  id="cld-billing-addr2"
                  value={billingAddr2}
                  onChange={setBillingAddr2}
                  placeholder="e.g. Suite 200"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <FieldLabel htmlFor="cld-billing-city" optional>City</FieldLabel>
                  <TextInput
                    id="cld-billing-city"
                    value={billingCity}
                    onChange={setBillingCity}
                    placeholder="e.g. Austin"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <FieldLabel htmlFor="cld-billing-state" optional>State</FieldLabel>
                  <TextInput
                    id="cld-billing-state"
                    value={billingState}
                    onChange={setBillingState}
                    placeholder="e.g. TX"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5 max-w-[200px]">
                <FieldLabel htmlFor="cld-billing-zip" optional>Zip Code</FieldLabel>
                <TextInput
                  id="cld-billing-zip"
                  value={billingZip}
                  onChange={setBillingZip}
                  placeholder="e.g. 78701"
                />
              </div>
            </div>
          )}
        </Section>

      </div>
    </div>
  );
}
